import * as React from 'react';
import {useEffect, useState} from 'react';
import {AlertCircle, Menu, X} from 'lucide-react';
import {useLocation, useNavigate} from "react-router-dom";
import AuthMenu from "./AuthMenu";
import CartBadge from "./CartBadge";
import BrandLogo from "./BrandLogo";
import {BRAND} from "../brand";

// Scrolls to a section on the home page; used by the hash links in the nav.
export const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({behavior: 'smooth', block: 'start'});
};

const Navbar: React.FC = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const navigateAssetReturn = () => navigate('/found');

    // Section links ("#talent") only exist on the home page, so from any other
    // route we go home first and let HomePage scroll to the hash; plain routes
    // ("/book") use the router instead of a full page load.
    const go = (href: string) => (e: React.MouseEvent) => {
        e.preventDefault();
        setIsMobileMenuOpen(false);
        if (href.startsWith('#')) {
            if (location.pathname === '/') {
                scrollToSection(href.slice(1));
                window.history.replaceState(null, '', href);
            } else {
                navigate({pathname: '/', hash: href});
            }
        } else {
            navigate(href);
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navLinks = [
        {name: 'Services', href: '#services'},
        {name: 'Talent', href: '#talent'},
        {name: 'Equipment', href: '/book'},
        {name: 'Insights', href: '#insights'},
        ...(BRAND.gallery ? [{name: 'Gallery', href: '#gallery'}] : []),
        {name: 'Contact', href: '/contact'},
    ];

    return (
        <nav
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                isScrolled ? 'glass-panel py-4' : 'bg-transparent py-6'
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
                {/* Logo */}
                <div className="flex items-center gap-2 cursor-pointer group" onClick={() => window.scrollTo(0, 0)}>
                    <div className="relative">
                        <BrandLogo
                            className="w-8 h-8 text-lumina-accent group-hover:animate-spin-slow transition-transform duration-700"/>
                        <div
                            className="absolute inset-0 bg-lumina-accent blur-lg opacity-40 group-hover:opacity-60 transition-opacity"></div>
                    </div>
                    <span className="text-2xl font-display font-bold tracking-widest text-white">
            {BRAND.wordmark}
          </span>
                </div>

                {/* Desktop Nav */}
                <div className="hidden md:flex items-center gap-8">
                    {navLinks.map((link) => (
                        <a
                            key={link.name}
                            href={link.href.startsWith('#') ? `/${link.href}` : link.href}
                            onClick={go(link.href)}
                            className="text-sm font-semibold tracking-wider text-gray-300 hover:text-lumina-accent transition-colors uppercase relative after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-0 after:h-0.5 after:bg-lumina-accent after:transition-all hover:after:w-full"
                        >
                            {link.name}
                        </a>
                    ))}
                    {/* Action cluster: tighter than the nav-link gap. */}
                    <div className="flex items-center gap-2 ml-2">
                        <button onClick={navigateAssetReturn}
                                className="inline-flex items-center gap-2 h-9 px-4 whitespace-nowrap text-xs font-bold uppercase tracking-wider text-red-400 border border-red-500/30 rounded hover:bg-red-500/10 transition-colors"
                        >
                            <AlertCircle className="w-3 h-3"/>
                            LOST ASSET?
                        </button>
                        <CartBadge/>
                        <AuthMenu/>
                    </div>
                </div>

                {/* Mobile Toggle */}
                <button
                    className="md:hidden text-white"
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                >
                    {isMobileMenuOpen ? <X/> : <Menu/>}
                </button>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
                <div className="md:hidden absolute top-full left-0 w-full glass-panel border-t border-gray-800">
                    <div className="flex flex-col p-6 gap-4">
                        {navLinks.map((link) => (
                            <a
                                key={link.name}
                                href={link.href.startsWith('#') ? `/${link.href}` : link.href}
                                className="text-lg font-display text-gray-200 hover:text-lumina-accent"
                                onClick={go(link.href)}
                            >
                                {link.name}
                            </a>
                        ))}
                        <a href="/found" onClick={go('/found')}
                           className="text-red-400 font-bold uppercase tracking-wider text-sm mt-4">
                            Report Lost Asset
                        </a>
                        <div className="mt-2">
                            <AuthMenu/>
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Navbar;